import { Box, Button, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Dayjs } from 'dayjs';
import moment from 'moment-timezone';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { createNewOrder } from '../../action/bookingsCommands';
import { fetchAllAssetGroups, fetchLocationAvailabilities } from '../../action/locationsCommands';
import { HeaderRightButtonContext } from '../../layout/MainLayout';
import { theme } from '../../material-ui/theme';
import { makeBookingDetailsUrl, paths } from '../../routes';
import { makeGuestBookingSelector } from '../../selector/bookingsSelector';
import {
    locationListSelector,
    makeLocationAssetGroupAvailabilities,
    makeLocationAssetGroups, makeLocationSelector,
} from '../../selector/locationsSelector';
import { areRequiredFieldsValid, getAvailableDurations, getAvailableTimeSlots } from '../../utils';
import { useLocationTimeZone } from '../../hooks/useLocationTimeZone';
import { visitorBookingWindow } from '../../utils/visitorBookingWindow';
import DatePickerValue from '../common/DateField';
import SelectComponent from '../common/SelectComponent';
import IssueReportLoaderOverlay from '../issueReports/IssueReportLoaderOverlay';
import IssueReportTextField from '../issueReports/IssueReportTextField';
import { generateSelectOptions } from '../issueReports/util/issueReportHelpers';
import BookingTicket from './BookingTicket';

const PREFIX = 'BookingPage';

const classes = {
    root: `${PREFIX}-root`,
    header: `${PREFIX}-header`,
    actions: `${PREFIX}-actions`,
    submitButton: `${PREFIX}-submitButton`,
    explanation: `${PREFIX}-explanation`,
    content: `${PREFIX}-content`,
    screenHeaderRight: `${PREFIX}-screenHeaderRight`,
    noteIcon: `${PREFIX}-noteIcon`,
};

const StyledBox = styled(Box)((
    {
        theme: any,
    },
) => ({
    [`&.${classes.root}`]: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '0 10px',
    },

    [`& .${classes.header}`]: {
        marginTop: '10px',
        paddingBottom: '40px',
    },

    [`& .${classes.actions}`]: {
        paddingTop: theme.spacing(1),
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    [`& .${classes.submitButton}`]: {
        margin: '0 auto',
        paddingTop: theme.spacing(1),
        width: '90%',
        maxWidth: 1000,
        padding: '12px 16px',
        boxSizing: 'border-box',
        position: 'fixed',
        bottom: 15,
        textTransform: 'none',
        fontSize: '20px',
        fontWeight: 'normal',
        borderRadius: '12px',
    },
    [`& .${classes.explanation}`]: {
        margin: `${theme.spacing(3)}px auto 0 auto`,
        maxWidth: 300,
    },
    [`& .${classes.content}`]: {
        flex: 1,
        marginBottom: '50px',
    },
}));

const enum BookingPages {
    creationPage,
    reservationPage
}

interface IssueReportDialogProps {

}

export interface ContactData {
    firstname: string;
    lastname: string;
    email: string;
    telephone: string;
    company: string;
    [key: string]: string;
}

interface BookingCreationPayload {
    locationId: string;
    assetGroupId: string;
    bookingDate: Dayjs | null;
    bookingTime: string;
    duration: string;
    contactData: ContactData;
    comment: string;
}

const VisitorBookingPage = (props: IssueReportDialogProps) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const [guestBookingCreated, setGuestBookingCreated] = useState<boolean>(false);
    const {setButtonState} = useContext(HeaderRightButtonContext);
    const [loading, setLoading] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(BookingPages.creationPage);
    const locations = useSelector(locationListSelector);
    const [bookingData, setBookingData] = useState<BookingCreationPayload>({
        locationId: '',
        assetGroupId: '',
        bookingDate: null,
        bookingTime: '',
        duration: '',
        contactData: {
            firstname: '',
            lastname: '',
            email: '',
            telephone: '',
            company: '',
        },
        comment: '',
    });
    const requiredFields = [
        'locationId',
        'assetGroupId',
        'bookingDate',
        'duration',
        'contactData.firstname',
        'contactData.lastname',
        'contactData.telephone',
        'contactData.company',
        'comment',
    ];

    const location = useSelector(makeLocationSelector(bookingData.locationId));
    const locationTimeZone = useLocationTimeZone(bookingData.locationId);
    // The window the order is created with (`createOrderFlow`), shown on the
    // location's clock (#3219) — the ticket and the stored booking cannot disagree.
    const reservationWindow = visitorBookingWindow(
        bookingData.bookingDate, bookingData.bookingTime, Number(bookingData.duration), locationTimeZone,
    );
    const reservationStartDate = reservationWindow
        ? moment.tz(reservationWindow.fromDate, locationTimeZone)
        : null;
    const reservationEndDate = reservationWindow
        ? moment.tz(reservationWindow.toDate, locationTimeZone)
        : null;

    const isSubmitDisabled = (currentPage === BookingPages.creationPage)
        && (!areRequiredFieldsValid(bookingData, requiredFields) || loading);

    const assetGroups = useSelector(makeLocationAssetGroups(bookingData.locationId));
    const assetGroup = assetGroups?.find((group) =>
        group.id === bookingData.assetGroupId);

    const guestBooking = useSelector(makeGuestBookingSelector());

    const createOrder = () => {
        dispatch(createNewOrder({
            groupId: bookingData.assetGroupId,
            fromDate: bookingData.bookingDate?.toDate()!,
            bookingTime: bookingData.bookingTime,
            duration: Number(bookingData.duration),
            contactData: bookingData.contactData,
            comment: bookingData.comment,
            isGuestBooking: true,
            timeZone: locationTimeZone,
        }));
    };

    useEffect(() => {
        setTimeout(() => {
            if (guestBooking?.bookingNumber && guestBookingCreated) {
                setLoading(false);
                navigate(makeBookingDetailsUrl(guestBooking.bookingNumber));
            }
        }, 1000);
    }, [guestBooking, guestBookingCreated]);

    const handleSubmit = () => {
        setLoading(true);
        if (currentPage === BookingPages.creationPage) {
            setLoading(false);
            setCurrentPage(BookingPages.reservationPage);
        } else {
            createOrder();
            setGuestBookingCreated(true);
        }
    };

    const filterAssetGroupsForLocation = (locationId: string) => {
        const location = locations.find(location => location.id === locationId);
        if (location) {
            return location?.assetGroups ?? [];
        }
        return [];
    };

    const personInfosKeys = [
        {
            key: 'firstname',
            label: t('visitor_booking.first_name'),
        },
        {
            key: 'lastname',
            label: t('visitor_booking.last_name'),
        },
        {
            key: 'telephone',
            label: t('visitor_booking.mobile_number'),
        },
        {
            key: 'company',
            label: t('visitor_booking.company_name'),
        },
    ];

    const groupAvailabilities = useSelector(
        makeLocationAssetGroupAvailabilities(
            bookingData.locationId,
            bookingData.assetGroupId ?? '',
        ),
    );

    let availableDates;
    if (groupAvailabilities) {
        availableDates = Object.keys(groupAvailabilities).filter(dateKey => {
            const timeSlots = groupAvailabilities[dateKey];
            return Object.values(timeSlots).some(slot => 
                Object.values(slot).some(value => value > 0),
            );
        });  
    }

    const availableDurations =
        groupAvailabilities && bookingData.bookingDate && bookingData.bookingTime
            ? getAvailableDurations(groupAvailabilities, bookingData.bookingDate, bookingData.bookingTime)
            : [];

    const availableDurationSelect = (durations: number[]) => {
        return durations.map((duration) => {
            if (duration < 1440) {
                return {
                    value: String(duration),
                    text: t('general.hours_val', { count: duration / 60 }),
                };
            } else {
                return {
                    value: String(duration),
                    text: t('general.days_val', {count: duration / 1440}),
                };
            }
        });
    };

    const updateHeaderRightButton = (status: boolean, route: string) => {
        setButtonState({
            isCancelButton: status,
            previousRoute: route,
        });
    };

    useEffect(() => {
        if (currentPage === BookingPages.reservationPage) {
            updateHeaderRightButton(true, paths.locations);
        }
    }, [currentPage]);

    useEffect(() => {
        dispatch(fetchAllAssetGroups({}));
    }, []);

    useEffect(() => {
        if (groupAvailabilities && assetGroup && assetGroup.isFullDay && bookingData.bookingDate) {
            const timeSlots = getAvailableTimeSlots(groupAvailabilities, bookingData.bookingDate);
            setBookingData({
                ...bookingData,
                bookingTime: timeSlots[0],
            });
        }
    }, [bookingData.bookingDate]);

    useEffect(() => {
        if (bookingData.locationId && bookingData.assetGroupId) {
            dispatch(fetchLocationAvailabilities({
                locationId: bookingData.locationId,
                assetGroupId: bookingData.assetGroupId,
                fromDate: moment().toDate(),
                toDate: moment().add(1, 'months').endOf('month').toDate(),
            }));
        }
    }, [bookingData.assetGroupId, bookingData.locationId]);

    return (
        <StyledBox className={classes.root}>
            {currentPage === BookingPages.creationPage &&
                <React.Fragment>
                    <Box className={classes.header}>
                        <Typography className={'text-2xl font-semibold text-center text-foreground'}>
                            {t('side_bar.visitor_booking')}
                        </Typography>
                    </Box>

                    <Box className={classes.content}>
                        <Box mb={2}>
                            <SelectComponent
                                options={generateSelectOptions(locations, ['id', 'name'])}
                                label={t('common.locations') + ' *'}
                                value={bookingData.locationId}
                                onChange={(value) => setBookingData({...bookingData, locationId: value})}
                            />
                        </Box>

                        <Box mb={2}>
                            <SelectComponent
                                options={generateSelectOptions(
                                    filterAssetGroupsForLocation(bookingData.locationId),
                                    ['id', 'name'])
                                }
                                label={t('visitor_booking.select_locker_size') + ' *'}
                                disabled={!bookingData.locationId}
                                value={bookingData.assetGroupId}
                                onChange={(value) => setBookingData({...bookingData, assetGroupId: value})}
                            />
                        </Box>

                        <Box mb={2}>
                            <DatePickerValue
                                label={t('visitor_booking.start_date') + ' *'}
                                value={bookingData.bookingDate}
                                disabled={!bookingData.assetGroupId || !bookingData.locationId}
                                availableDates={availableDates}
                                setValue={(date: any) => setBookingData({
                                    ...bookingData,
                                    bookingDate: date,
                                })}
                            />
                        </Box>

                        <Box mb={2}>
                            <SelectComponent
                                autoComplete={true}
                                options={availableDurationSelect(availableDurations)}
                                label={t('visitor_booking.duration') + ' *'}
                                value={bookingData.duration}
                                disabled={!bookingData.bookingDate}
                                onChange={(value) => setBookingData({...bookingData, duration: value})}
                            />
                        </Box>

                        {personInfosKeys.map((info) =>
                            <Box key={info.key} mb={2}>
                                <IssueReportTextField
                                    numsRows={1}
                                    title={info.label + ' *'}
                                    value={bookingData.contactData[info.key]}
                                    onChange={(value) => setBookingData({
                                        ...bookingData,
                                        contactData: {
                                            ...bookingData.contactData,
                                            [info.key]: value,
                                        },
                                    })}
                                />
                            </Box>,
                        )}
                        <Box mb={2}>
                            <IssueReportTextField
                                numsRows={1}
                                title={t('visitor_booking.booking_comment') + ' *'}
                                value={bookingData.comment}
                                onChange={(value) => setBookingData({
                                    ...bookingData, comment: value,
                                })}
                            />
                        </Box>
                    </Box>
                </React.Fragment>
            }
            {currentPage === BookingPages.reservationPage &&
                <React.Fragment>
                    <Typography className={'text-center text-foreground font-semibold text-2xl mb-8'}>
                        {t('visitor_booking.ticket.header')}</Typography>
                    <BookingTicket
                        location={location?.name}
                        size={assetGroup?.name}
                        reservationStartDate={reservationStartDate}
                        reservationEndDate={reservationEndDate}
                        amount={1}
                    />
                </React.Fragment>
            }
            <Box className={classes.actions}>
                <Button
                    variant={'contained'}
                    disabled={isSubmitDisabled}
                    onClick={handleSubmit}
                    className={classes.submitButton + 
                        ' bg-foreground text-background hover:border-foreground hover:border '
                        + 'disabled:border disabled:border-foreground/20'}
                >
                    {currentPage === BookingPages.creationPage
                        ? t('visitor_booking.next_button')
                        : t('visitor_booking.confirm_booking')}
                </Button>
            </Box>
            <IssueReportLoaderOverlay open={loading} />
        </StyledBox>
    );
};

export default VisitorBookingPage;
